import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './Slider.module.css'

interface SliderProps {
  value: number
  onChange?: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean
  fillColor?: string
  thumbColor?: string
  trackColor?: string
}

export default function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  disabled = false,
  fillColor = '#ffffff',
  thumbColor = '#ffffff',
  trackColor = 'rgba(255, 255, 255, 0.3)'
}: SliderProps) {
  const [isDragging, setIsDragging] = useState(false)
  const sliderRef = useRef<HTMLDivElement>(null)

  const normalizedValue = Math.max(min, Math.min(max, value))
  const percentage = ((normalizedValue - min) / (max - min)) * 100

  const updateValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current || disabled || !onChange) return
    const rect = sliderRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    const percent = x / rect.width
    const newValue = Math.round(min + percent * (max - min))
    onChange(Math.max(min, Math.min(max, newValue)))
  }, [disabled, onChange, min, max])

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return
    setIsDragging(true)
    updateValueFromPosition(e.clientX)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return
    setIsDragging(true)
    if (e.touches.length > 0) {
      updateValueFromPosition(e.touches[0].clientX)
    }
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      updateValueFromPosition(e.clientX)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        updateValueFromPosition(e.touches[0].clientX)
      }
    }

    const handleEnd = () => {
      setIsDragging(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleEnd)
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleEnd)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleEnd)
    }
  }, [isDragging, updateValueFromPosition])

  return (
    <div
      ref={sliderRef}
      className={`${styles.sliderContainer} ${disabled ? styles.disabled : ''}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div
        className={styles.track}
        style={{ background: trackColor }}
      >
        <div
          className={styles.fill}
          style={{
            width: `${percentage}%`,
            background: fillColor
          }}
        />
        <div
          className={styles.thumb}
          style={{
            left: `${percentage}%`,
            background: thumbColor,
            borderColor: fillColor
          }}
        />
      </div>
    </div>
  )
}
